import React, { useCallback, useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import {
  UsersIcon,
  RefreshIcon,
  UserPlusIcon,
  CancelIcon,
  SaveIcon,
  TrashIcon,
} from "../components/icons";

// Owner-only member management. Lists every active/disabled member, lets
// the owner invite new members with an initial password, change roles,
// reset passwords, or disable.
const ROLES = ["owner", "admin", "assistant"];

const rolePill = (role) => {
  const map = {
    owner: "bg-purple-100 text-purple-800",
    admin: "bg-blue-100 text-blue-800",
    assistant: "bg-gray-100 text-gray-800",
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
        map[role] || "bg-gray-100 text-gray-800"
      }`}
    >
      {role}
    </span>
  );
};

const BureauMembers = () => {
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "assistant",
  });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [editing, setEditing] = useState(null); // member being edited
  const [editDraft, setEditDraft] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading members…");
      setGlobalLoading(true);
      const [profile, list] = await Promise.all([
        apiFetch("/auth/profile"),
        apiFetch("/members"),
      ]);
      setMe(profile?.member || null);
      setMembers(list?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load members");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const isOwner = me?.role === "owner";

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!invite.fullName.trim() || !invite.email.trim() || !invite.password) {
      setError("Full name, email, and password are required");
      return;
    }
    if (invite.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setInviteBusy(true);
    try {
      await apiFetch("/members", {
        method: "POST",
        body: JSON.stringify(invite),
      });
      setInvite({ fullName: "", email: "", password: "", role: "assistant" });
      setInviteOpen(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to invite member");
    } finally {
      setInviteBusy(false);
    }
  };

  const startEdit = (m) => {
    setEditing(m);
    setEditDraft({
      fullName: m.fullName,
      role: m.role,
      status: m.status,
      password: "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditBusy(true);
    try {
      const body = {
        fullName: editDraft.fullName,
        role: editDraft.role,
        status: editDraft.status,
      };
      if (editDraft.password) body.password = editDraft.password;
      await apiFetch(`/members/${editing._id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message || "Failed to update member");
    } finally {
      setEditBusy(false);
    }
  };

  const disable = async (m) => {
    if (
      !window.confirm(
        `Disable "${m.fullName}"? They will no longer be able to sign in.`
      )
    ) {
      return;
    }
    try {
      await apiFetch(`/members/${m._id}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setError(err.message || "Failed to disable member");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Bureau Team
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Owners, admins, and assistants who can sign in to operate this bureau
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              {loading ? "Loading…" : "Refresh"}
            </button>
            {isOwner && (
              <button
                onClick={() => setInviteOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <UserPlusIcon className="w-4 h-4" />
                Invite member
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!isOwner && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
            You can view members but only an owner can invite or change roles.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => (
            <div
              key={m._id}
              className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                    {m.fullName}
                    {me?.id === m._id && (
                      <span className="ml-2 text-[10px] font-normal text-[var(--color-text-muted)]">
                        (you)
                      </span>
                    )}
                  </h3>
                  <div className="text-xs text-[var(--color-text-muted)] truncate">
                    {m.email}
                  </div>
                </div>
                {rolePill(m.role)}
              </div>
              <div className="text-[10px] text-[var(--color-text-muted)]">
                Status: {m.status}
                {m.lastLoginAt && (
                  <> · Last login {new Date(m.lastLoginAt).toLocaleString()}</>
                )}
              </div>
              {isOwner && (
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => startEdit(m)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-2 py-1 text-xs font-medium transition-colors"
                  >
                    Edit
                  </button>
                  {m.status === "active" && me?.id !== m._id && (
                    <button
                      onClick={() => disable(m)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-red-50 hover:border-red-300 text-red-600 px-2 py-1 text-xs font-medium transition-colors"
                    >
                      <TrashIcon className="w-3 h-3" />
                      Disable
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {!loading && members.length === 0 && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            No members yet.
          </div>
        )}
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <form
            onSubmit={handleInvite}
            className="w-full max-w-md bg-[var(--color-surface)] rounded-xl shadow-lg p-5 space-y-3"
          >
            <h3 className="text-lg font-semibold text-[var(--color-text-strong)]">
              Invite member
            </h3>
            <p className="text-xs text-[var(--color-text-muted)]">
              Set an initial password and share it with them out-of-band. They
              can change it from their profile.
            </p>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Full name
              </label>
              <input
                value={invite.fullName}
                onChange={(e) =>
                  setInvite((s) => ({ ...s, fullName: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Email
              </label>
              <input
                type="email"
                value={invite.email}
                onChange={(e) =>
                  setInvite((s) => ({ ...s, email: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Initial password (min 8 chars)
              </label>
              <input
                type="text"
                value={invite.password}
                onChange={(e) =>
                  setInvite((s) => ({ ...s, password: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-mono"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Role
              </label>
              <select
                value={invite.role}
                onChange={(e) =>
                  setInvite((s) => ({ ...s, role: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-sm font-medium transition-colors"
              >
                <CancelIcon className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteBusy}
                className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <UserPlusIcon className="w-4 h-4" />
                {inviteBusy ? "Inviting…" : "Invite"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md bg-[var(--color-surface)] rounded-xl shadow-lg p-5 space-y-3">
            <h3 className="text-lg font-semibold text-[var(--color-text-strong)]">
              Edit member
            </h3>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Full name
              </label>
              <input
                value={editDraft.fullName || ""}
                onChange={(e) =>
                  setEditDraft((s) => ({ ...s, fullName: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Role
              </label>
              <select
                value={editDraft.role || "assistant"}
                onChange={(e) =>
                  setEditDraft((s) => ({ ...s, role: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Status
              </label>
              <select
                value={editDraft.status || "active"}
                onChange={(e) =>
                  setEditDraft((s) => ({ ...s, status: e.target.value }))
                }
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Reset password (optional, min 8 chars)
              </label>
              <input
                type="text"
                value={editDraft.password || ""}
                onChange={(e) =>
                  setEditDraft((s) => ({ ...s, password: e.target.value }))
                }
                placeholder="Leave blank to keep current password"
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-mono"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-sm font-medium transition-colors"
              >
                <CancelIcon className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={editBusy}
                className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <SaveIcon className="w-4 h-4" />
                {editBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BureauMembers;
